import React, { useState } from 'react';
import { courses } from '../data/mock';
import { Clock, MapPin, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Courses = () => {
  const [filter, setFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All Courses' },
    { id: 'primary', label: 'Primary (1-5)' },
    { id: 'middle', label: 'Middle (6-8)' },
    { id: 'secondary', label: 'Secondary (9-10)' },
    { id: 'senior', label: 'Senior (11-12)' }
  ];

  const getFilteredCourses = () => {
    if (filter === 'all') return courses;
    if (filter === 'primary') return courses.filter(c => c.id === 1);
    if (filter === 'middle') return courses.filter(c => c.id === 2);
    if (filter === 'secondary') return courses.filter(c => c.id === 3);
    if (filter === 'senior') return courses.filter(c => [4, 5, 6, 7].includes(c.id));
    return courses;
  };

  const filteredCourses = getFilteredCourses();

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="heading-hero mb-4">Our Courses</h1>
          <p className="body-large text-[var(--text-secondary)] max-w-2xl mx-auto mb-8">
            Comprehensive coaching programs for Classes 1-12 covering CBSE, ICSE, and State Boards
          </p>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-6 py-2 rounded-full font-mono text-sm uppercase tracking-wider transition-all ${
                  filter === f.id
                    ? 'bg-[var(--text-primary)] text-white'
                    : 'bg-white border border-[var(--border-primary)] text-[var(--text-primary)] hover:border-[var(--text-primary)]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredCourses.map((course) => (
            <div key={course.id} className={`course-card ${course.color}`}>
              <div className="mb-3">
                <span className="font-mono text-xs uppercase tracking-wider text-[var(--text-muted)]">
                  {course.grades}
                </span>
              </div>
              
              <h3 className="heading-2 mb-3">{course.title}</h3>
              
              <p className="body-small text-[var(--text-secondary)] mb-4">
                {course.description}
              </p>

              {/* Subjects */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-2">
                  Subjects Covered:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {course.subjects.map((subject) => (
                    <span
                      key={subject}
                      className="text-xs px-3 py-1 bg-white/60 rounded-full text-[var(--text-secondary)]"
                    >
                      {subject}
                    </span>
                  ))}
                </div>
              </div>

              {/* Duration & Mode */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <MapPin size={16} />
                  <span>{course.mode.join(', ')}</span>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-auto pt-4 border-t border-black/10">
                <Link
                  to="/contact"
                  className="w-full btn-primary text-center"
                >
                  Enroll Now
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="bg-[var(--bg-card)] rounded-2xl p-8 shadow-sm">
          <h2 className="heading-2 mb-6 text-center">What's Included in Every Course</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Experienced Faculty',
              'Small Batch Size (10-15 Students)',
              'Comprehensive Study Material',
              'Regular Mock Tests',
              'Doubt Solving Sessions',
              'One-to-One Mentoring',
              'Monthly Progress Reports'
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <CheckCircle size={20} className="text-[var(--accent-green-200)] flex-shrink-0" style={{ color: '#6b8f6d' }} />
                <span className="body-small text-[var(--text-secondary)]">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center bg-[var(--gradient-hero-subtle)] rounded-2xl p-12">
          <h2 className="heading-1 mb-4">Not Sure Which Course to Choose?</h2>
          <p className="body-medium text-[var(--text-secondary)] mb-6 max-w-2xl mx-auto">
            Get in touch with us for personalized course recommendations based on your child's needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Book Free Counseling
            </Link>
            <a href="tel:+917004929531" className="btn-secondary">
              Call Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Courses;
